import { FormGroup, FormParagraph, FormSection } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOrganization } from '../../../interfaces/organization';
import type { IPractitioner } from '../../../interfaces/practitioner';
import type { ISubsequentService } from '../../../interfaces/subsequentService';
import type {
    IAutoSubsequentServiceEditVersion,
    IPersonalSettingsCarePathServiceConsumerSettingFormSectionProps,
} from './PersonalSettingsCarePathServiceConsumerSetting';
import { ServiceType } from '../../../../../common/enums/serviceType';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { parseNameForSearch } from '../../../../../common/utils/parseNameForSearch';
import { AbortableFetch } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { InputTriState } from '../../../elements/common';
import { DropdownAsyncLoadInput } from '../../../fields/DropdownAsyncLoad';
import { SettingsSwitch } from '../../carePath/modals/components/SettingsSwitch';

interface ICloseServiceAndSubsequentServiceSettingsProps
    extends IPersonalSettingsCarePathServiceConsumerSettingFormSectionProps {
    employmentId: string;
    possibleSubsequentServices: ISubsequentService[];
}

export interface IServiceOrganization {
    id: string;
    isRequestRecipient: boolean;
    name: string;
}

export function CloseServiceAndSubsequentServiceSettings(props: ICloseServiceAndSubsequentServiceSettingsProps) {
    const abortableFetch = new AbortableFetch();
    const [subsequentServices, setSubsequentServices] = useState<ISubsequentService[]>([]);
    const [selectedSubsequentService, setSelectedSubsequentService] = useState<ISubsequentService | null>();
    const [selectedProviderOrganization, setSelectedProviderOrganization] = useState<string | null>();
    const [selectedProviderPractitioner, setSelectedProviderPractitioner] = useState<string | null>();
    const [autoSubsequentService, setAutoSubsequentService] = useState<IAutoSubsequentServiceEditVersion>();
    const [isProviderPractitionerRequired, setIsProviderPractitionerRequired] = useState(false);
    const [serviceOrganizations, setServiceOrganizations] = useState<IServiceOrganization[]>([]);

    if (props.service.serviceType === ServiceType.contact) {
        return null;
    }

    useEffect(() => {
        loadSubsequentServices();
    }, [props.employmentId, props.service.id]);

    useEffect(() => {
        if (props.autoSubsequentService?.subsequentServiceId) {
            setSelectedSubsequentService(findSubsequentService(props.autoSubsequentService.subsequentServiceId));
        }

        if (props.mode === 'edit') {
            if (props.autoSubsequentService?.providerOrganizationId) {
                setSelectedProviderOrganization(props.autoSubsequentService.providerOrganizationId);
            }
            if (props.autoSubsequentService?.providerPractitionerId) {
                setSelectedProviderPractitioner(props.autoSubsequentService.providerPractitionerId);
            }
        }
    }, [
        props.mode,
        props.autoSubsequentService?.subsequentServiceId,
        props.autoSubsequentService?.providerOrganizationId,
        props.autoSubsequentService?.providerPractitionerId,
        subsequentServices,
    ]);

    useEffect(() => {
        if (selectedProviderOrganization) {
            isProviderPractitionerRequiredForOrganization(selectedProviderOrganization);
        }
    }, [props.mode, selectedProviderOrganization, selectedProviderPractitioner, selectedSubsequentService]);

    useEffect(() => {
        if (props.mode === 'edit') {
            setAutoSubsequentService(props.autoSubsequentService);
        }
    }, [props.mode, props.autoSubsequentService]);

    if (!showHasSubsequentServices() && !showAutoCloseRequest()) {
        return null;
    }

    return (
        <FormSection title={translateKey('addCarePathSettingsHeaders.closeServiceAndSubsequentService')}>
            {showAutoCloseRequest() && (
                <FormGroup label={translateKey('addCarePathSettingsLabelsServiceConsumer.isAutoCloseRequest')}>
                    {props.mode === 'read' ? (
                        <FormParagraph>
                            {props.isAutoCloseRequest ? translateKey('common.yes') : translateKey('common.no')}
                        </FormParagraph>
                    ) : (
                        <InputTriState
                            fieldKey="isAutoCloseRequest"
                            notNullable
                        />
                    )}
                </FormGroup>
            )}

            {showHasSubsequentServices() && (
                <>
                    <FormGroup
                        label={translateKey('addCarePathSettingsLabelsServiceConsumer.addAutoSubsequentServices')}
                        tooltipText={translateKey('personalSettings.subsequentServiceTooltip')}
                    >
                        {props.mode === 'read' ? (
                            <FormParagraph>
                                {props.hasAutoSubsequentService
                                    ? translateKey('common.yes')
                                    : translateKey('common.no')}
                            </FormParagraph>
                        ) : (
                            <SettingsSwitch
                                disabled={!subsequentServices.length}
                                listKey="hasAutoSubsequentService"
                                onChange={(value) => props.handleChange('hasAutoSubsequentService', value)}
                                value={props.hasAutoSubsequentService}
                            />
                        )}
                    </FormGroup>

                    {showSucceedingServiceDetails() && (
                        <>
                            <FormGroup label={translateKey('subsequentService.autoSubsequentServices')}>
                                {props.mode === 'read' ? (
                                    <FormParagraph>
                                        {selectedSubsequentService?.succeedingService?.name || '-'}
                                    </FormParagraph>
                                ) : (
                                    <DropdownAsyncLoadInput<string>
                                        error={props.errors.get('autoSubsequentService.subsequentServiceId')}
                                        fieldKey="autoSubsequentService"
                                        loadOptions={getLabelledSubsequentServices}
                                        onChange={async (value) => {
                                            setSelectedSubsequentService(findSubsequentService(value));
                                            setSelectedProviderOrganization(null);
                                            setSelectedProviderPractitioner(null);
                                            setAutoSubsequentService(
                                                getAutoSubsequentObject('subsequentServiceId', value)
                                            );
                                            props.handleChange(
                                                'autoSubsequentService',
                                                getAutoSubsequentObject('subsequentServiceId', value)
                                            );
                                        }}
                                        placeholder={translateKey('common.clickAndChooseFromList')}
                                        value={autoSubsequentService?.subsequentServiceId}
                                    />
                                )}
                            </FormGroup>

                            <FormGroup label={translateKey('subsequentService.isDeviation')}>
                                {props.mode === 'read' ? (
                                    <FormParagraph>
                                        {props.autoSubsequentService?.isDeviation
                                            ? translateKey('common.yes')
                                            : translateKey('common.no')}
                                    </FormParagraph>
                                ) : (
                                    <InputTriState
                                        fieldKey="autoSubsequentService"
                                        notNullable
                                        onChange={(value) => {
                                            setAutoSubsequentService(getAutoSubsequentObject('isDeviation', value));
                                            props.handleChange(
                                                'autoSubsequentService',
                                                getAutoSubsequentObject('isDeviation', value)
                                            );
                                        }}
                                        value={autoSubsequentService?.isDeviation}
                                    />
                                )}
                            </FormGroup>
                            {showProviderOrganization() && (
                                <>
                                    <FormGroup
                                        isRequired={props.mode === 'edit'}
                                        label={translateKey(
                                            'addCarePathSettingsLabelsServiceConsumer.providerOrganizationId'
                                        )}
                                    >
                                        {props.mode === 'read' ? (
                                            <FormParagraph>
                                                {props.autoSubsequentService?.providerOrganization?.name || '-'}
                                            </FormParagraph>
                                        ) : (
                                            <DropdownAsyncLoadInput<string>
                                                error={props.errors.get('autoSubsequentService.providerOrganizationId')}
                                                fieldKey="autoSubsequentService"
                                                loadOptions={loadServiceProviderOrganizations}
                                                onChange={async (value) => {
                                                    setSelectedProviderOrganization(value);
                                                    setSelectedProviderPractitioner(null);
                                                    isProviderPractitionerRequiredForOrganization(value);
                                                    setAutoSubsequentService(
                                                        getAutoSubsequentObject('providerOrganizationId', value)
                                                    );
                                                    props.handleChange(
                                                        'autoSubsequentService',
                                                        getAutoSubsequentObject('providerOrganizationId', value)
                                                    );
                                                }}
                                                placeholder={translateKey('subsequentService.selectOrganization')}
                                                value={selectedProviderOrganization}
                                            />
                                        )}
                                    </FormGroup>

                                    {showProviderPractitioner() && (
                                        <FormGroup
                                            isRequired={props.mode === 'edit' && isProviderPractitionerRequired}
                                            label={translateKey(
                                                'addCarePathSettingsLabelsServiceConsumer.providerPractitionerId'
                                            )}
                                        >
                                            {props.mode === 'read' ? (
                                                <FormParagraph>
                                                    {props.autoSubsequentService?.providerPractitionerId &&
                                                    props.autoSubsequentService.providerPractitioner
                                                        ? getDisplayName(
                                                              props.autoSubsequentService.providerPractitioner
                                                          )
                                                        : '-'}
                                                </FormParagraph>
                                            ) : (
                                                <DropdownAsyncLoadInput<string>
                                                    error={props.errors.get(
                                                        'autoSubsequentService.providerPractitionerId'
                                                    )}
                                                    fieldKey="autoSubsequentService"
                                                    loadOptions={loadServiceProviderPractitioners}
                                                    onChange={async (value) => {
                                                        setSelectedProviderPractitioner(value);
                                                        setAutoSubsequentService(
                                                            getAutoSubsequentObject('providerPractitionerId', value)
                                                        );
                                                        props.handleChange(
                                                            'autoSubsequentService',
                                                            getAutoSubsequentObject('providerPractitionerId', value)
                                                        );
                                                    }}
                                                    placeholder={translateKey('subsequentService.selectProvider')}
                                                    refreshToken={selectedProviderOrganization || 'default'}
                                                    value={selectedProviderPractitioner}
                                                />
                                            )}
                                        </FormGroup>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </FormSection>
    );

    async function loadSubsequentServices() {
        const url = `/api/subsequentServices?employmentId=${props.employmentId}&isActive=true&serviceId=${props.service.id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const subsequentServiceResults = result.json.items as ISubsequentService[];
        setSubsequentServices(subsequentServiceResults);
    }

    async function getLabelledSubsequentServices() {
        const subsequentServicesLabelled = subsequentServices.map((subsequentService: ISubsequentService) => ({
            value: subsequentService.id,
            label: subsequentService.succeedingService?.name || '',
        }));

        return subsequentServicesLabelled;
    }

    async function loadServiceProviderOrganizations(searchInput?: string) {
        if (!selectedSubsequentService?.succeedingService && !selectedSubsequentService?.succeedingServiceId) {
            return [];
        }
        const searchText = searchInput ? `&searchText=${encodeURIComponent(parseNameForSearch(searchInput))}` : '';

        const isBiometrician =
            selectedSubsequentService?.succeedingService?.serviceType === ServiceType.examination
                ? '&isBiometrician=true'
                : '';
        const isReferral =
            selectedSubsequentService?.succeedingService?.serviceType === ServiceType.referral
                ? '&isReferral=true'
                : '';
        const isReferralSmall =
            selectedSubsequentService?.succeedingService?.serviceType === ServiceType.referralSmall
                ? '&isReferralSmall=true'
                : '';

        const url = `/api/services/${selectedSubsequentService?.succeedingServiceId}/serviceProviders/organizationsAndPractitioners?${isBiometrician}${searchText}${isReferral}${isReferralSmall}&page=1&limit=1000`;

        const result = await abortableFetch.fetchJson(url, 'GET');
        const serviceProviderOrganizations = result.json.items.map(
            ({
                id,
                name,
                isOrganizationRequestRecipient,
            }: {
                id: string;
                isOrganizationRequestRecipient: boolean;
                name: string;
            }) => ({ id, name, isRequestRecipient: isOrganizationRequestRecipient })
        );
        setServiceOrganizations(serviceProviderOrganizations);

        return serviceProviderOrganizations.map((organization: IOrganization) => {
            return {
                value: organization.id,
                label: organization.name,
            };
        });
    }

    async function loadServiceProviderPractitioners(searchText: string) {
        if (!selectedProviderOrganization) {
            return [];
        }

        let url = `/api/serviceProvidersList/${selectedSubsequentService?.succeedingServiceId}/practitioners?isRequestRecipient=true&organizationId=${selectedProviderOrganization}${
            searchText ? `&searchText=${searchText}` : ''
        }`;
        if (selectedSubsequentService?.succeedingService?.serviceType === ServiceType.examination) {
            url += '&isBiometrician=true';
        }
        const result = await abortableFetch.fetchJson(url, 'GET');

        return result.json.items.map((practitioner: IPractitioner) => {
            return {
                value: practitioner.id,
                label: getDisplayName(practitioner),
            };
        });
    }

    function findSubsequentService(subsequentServiceId: string | null) {
        return subsequentServices.find((service) => service.id === subsequentServiceId);
    }

    function showAutoCloseRequest() {
        return (
            props.service.serviceType &&
            [ServiceType.examination, ServiceType.consultation, ServiceType.referral].includes(
                props.service.serviceType
            )
        );
    }

    function showHasSubsequentServices() {
        return props.possibleSubsequentServices.length > 0;
    }

    function showSucceedingServiceDetails() {
        return (
            (props.mode === 'read' && props.hasAutoSubsequentService) ||
            (props.mode === 'edit' && props.hasAutoSubsequentService && !!subsequentServices.length)
        );
    }

    function showProviderOrganization() {
        return props.mode === 'read' || !!selectedSubsequentService;
    }

    function showProviderPractitioner() {
        return props.mode === 'read' || !!selectedProviderOrganization;
    }

    function getAutoSubsequentObject(key: keyof IAutoSubsequentServiceEditVersion, value: any) {
        const updatedAutoSubsequentService = {
            isDeviation: !!autoSubsequentService?.isDeviation,
            subsequentServiceId: autoSubsequentService?.subsequentServiceId,
            providerOrganizationId: autoSubsequentService?.providerOrganizationId,
            providerPractitionerId: autoSubsequentService?.providerPractitionerId,
            subsequentService: props.autoSubsequentService?.subsequentService,
        };

        if (key === 'isDeviation') {
            updatedAutoSubsequentService.isDeviation = value;
        } else if (key === 'subsequentServiceId') {
            updatedAutoSubsequentService.subsequentServiceId = value;
            updatedAutoSubsequentService.providerOrganizationId = undefined;
            updatedAutoSubsequentService.providerPractitionerId = undefined;
        } else if (key === 'providerOrganizationId') {
            updatedAutoSubsequentService.providerOrganizationId = value;
            updatedAutoSubsequentService.providerPractitionerId = undefined;
        } else if (key === 'providerPractitionerId') {
            updatedAutoSubsequentService.providerPractitionerId = value;
        }

        return updatedAutoSubsequentService as IAutoSubsequentServiceEditVersion;
    }

    function isProviderPractitionerRequiredForOrganization(organizationId: string | null) {
        if (organizationId) {
            const selectedOrganization = serviceOrganizations.find(
                (organization) => organization.id === organizationId
            );

            if (selectedOrganization) {
                setIsProviderPractitionerRequired(!selectedOrganization.isRequestRecipient);
            }
        }
    }
}
